/**
 * Pomodoro Timer Display — circular ring or flat card.
 * Centered, large, dominant — the hero of the screen.
 */

import React, { useMemo } from 'react'
import { motion } from 'framer-motion'
import { usePomodoroStore } from '@turnifly/shared/stores'

export const PomodoroTimer: React.FC = () => {
  const timerStyle = usePomodoroStore((s) => s.settings.timerStyle)
  if (timerStyle === 'flat') return <FlatTimer />
  return <CircularTimer />
}

// ── Circular Timer ──

const R = 130
const SW = 5
const C = 2 * Math.PI * R
const SIZE = (R + SW + 4) * 2

const CircularTimer: React.FC = () => {
  const timeRemaining = usePomodoroStore((s) => s.timeRemaining)
  const mode = usePomodoroStore((s) => s.mode)
  const workDuration = usePomodoroStore((s) => s.settings.workDuration)
  const shortBreakDuration = usePomodoroStore((s) => s.settings.shortBreakDuration)
  const longBreakDuration = usePomodoroStore((s) => s.settings.longBreakDuration)

  const total = useMemo((): number => {
    if (mode === 'work') return workDuration * 60
    if (mode === 'short_break') return shortBreakDuration * 60
    return longBreakDuration * 60
  }, [mode, workDuration, shortBreakDuration, longBreakDuration])

  const progress = total > 0 ? timeRemaining / total : 1
  const offset = C * (1 - progress)
  const time = fmt(timeRemaining)
  const center = SIZE / 2

  return (
    <div className="relative flex items-center justify-center mb-6">
      <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
        {/* Track */}
        <circle
          cx={center}
          cy={center}
          r={R}
          fill="none"
          stroke="rgba(255,255,255,0.08)"
          strokeWidth={SW}
        />
        {/* Progress arc */}
        <motion.circle
          cx={center}
          cy={center}
          r={R}
          fill="none"
          stroke="rgba(255,255,255,1)"
          strokeWidth={SW}
          strokeLinecap="round"
          strokeDasharray={C}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${center} ${center})`}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          style={{ filter: "drop-shadow(0 0 12px rgba(255,255,255,0.6))" }}
        />
      </svg>

      {/* Time */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-[64px] font-display font-medium text-white tabular-nums tracking-tight leading-none drop-shadow-[0_4px_24px_rgba(0,0,0,0.4)]">
          {time}
        </span>
      </div>
    </div>
  )
}

// ── Flat Timer ──

const FlatTimer: React.FC = () => {
  const timeRemaining = usePomodoroStore((s) => s.timeRemaining)
  const time = fmt(timeRemaining)

  return (
    <div className="mb-6">
      <div className="px-16 py-12 rounded-[32px] bg-white/[0.08] backdrop-blur-2xl border border-white/[0.15] shadow-[0_8px_32px_rgba(0,0,0,0.12)]">
        <span className="block text-[80px] font-display font-medium text-white tabular-nums tracking-tight text-center leading-none drop-shadow-[0_4px_24px_rgba(0,0,0,0.4)]">
          {time}
        </span>
      </div>
    </div>
  )
}

function fmt(sec: number): string {
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
}
